// SEO-12 · /resources/school-erp-evaluation-checklist-india
//
// EDITORIAL GROUNDING (see docs/marketing/RESOURCES_EDITORIAL_PROCESS.md):
//   • The "hard for us" section is written straight off the ⛔ and ⚠️ rows of
//     docs/marketing/CLAIMS_REGISTRY.md — S9 (no certification to name), S10 (no uptime/SLA/
//     service credits/response-time promises), S11 (no customer counts or testimonials),
//     MOB1/MOB3 (controlled early access, not on the stores), MOB4/I-13 (no offline-first),
//     DR1 (deployment region is documented per deployment, never inferred from this website),
//     I8 (Aadhaar e-KYC not marketed), I5 (DigiLocker adapter is not live-provider evidence),
//     D2 (statutory outputs from a locked payroll run; filing stays a school action).
//   • Cost-transparency questions map to C4/C5/C6 (published MDR + platform fee, integration and
//     per-message schedules) and X1/I1 (collection through the school's own gateway account).
//   • Control questions map to S2 (RBAC + record-level teacher scope + maker-checker on money)
//     and S6 (hash-chained, DB-immutable audit).
//   • PS4 (CSV/XLSX exports) and PS6 (per-channel evidence; SMS must not claim read evidence).
// This article names our own weak answers on purpose. Do not soften them without a registry change.

const article = {
  slug: "school-erp-evaluation-checklist-india",
  title: "What to check before you buy school ERP software in India",
  seoTitle: "School ERP Evaluation Checklist for India | Vidyapeeth360",
  description:
    "An evaluation checklist for Indian school leaders buying an ERP — the money, access, data and messaging questions a demo will not answer.",
  topic: "Buying guide",
  topicKey: "buying",
  readingMinutes: 7,
  published: "2026-08-18",
  updated: "2026-08-18",
  standfirst:
    "Every school ERP demo shows the same forty minutes of happy path. These are the questions that reveal the other eleven months — including a section where our own answers are the weakest.",
  moduleLinks: ["fee-collection", "role-and-permission", "mis-reports"],
  conversion: {
    to: "/pricing",
    label: "Check our published pricing and cost schedule",
    note: "Subscription, gateway charges, integration fees and per-message rates are on the page, not in a quote.",
  },
  sections: [
    {
      id: "how-to-run-it",
      heading: "Run the evaluation on your data, not their demo",
      blocks: [
        {
          t: "p",
          text: "A vendor demo is a rehearsed route through a clean database. It says little about how the product behaves on a Monday in July with your class structure and the parent who paid the wrong sibling's invoice. Before you look at a screen, prepare three things: one class's real student list with the messy rows left in, one term's real fee structure, and the five jobs your office does weekly that take too long.",
        },
        {
          t: "p",
          text: "Then ask the vendor to do those jobs in front of you, on that data. The questions below are what you ask while they work.",
        },
      ],
    },
    {
      id: "money",
      heading: "Money",
      blocks: [
        {
          t: "ul",
          items: [
            "**Whose payment gateway account is it?** Does a parent's online payment land in an account in the school's name, or pass through the vendor's first? That decides who controls settlement and what happens if you leave. Ask to see the account name on a settlement report.",
            "**What is the complete cost sheet?** Ask for the gateway charge per method — credit, debit, UPI, net banking, EMI — any per-transaction platform fee, one-time integration charges, and the per-unit price of each message channel. Then model your volume.",
            "**How is money stored?** Whole paise or decimals? A system doing floating-point arithmetic on fees produces totals that are approximately right forever.",
            "**Show me a reprinted receipt.** Ask for a receipt printed today for last month's payment. It should be visibly marked a duplicate — otherwise one receipt can be presented twice as two payments.",
            "**Can a person approve their own refund?** Ask them to try. Refunds, concessions and payroll runs should need a second person — if the demo account can do it all alone, so can your fee clerk.",
            "**Can anything be back-dated into a closed month?** Record a receipt dated before your last closing. \"It just saves\" is the wrong answer.",
          ],
        },
      ],
    },
    {
      id: "access",
      heading: "Access and control",
      blocks: [
        {
          t: "ul",
          items: [
            "**What can a class teacher see?** Ask to log in as one. A subject teacher should not open another section's marks, and no teacher should browse the fee ledger or medical notes.",
            "**Do permissions apply to reading, or only editing?** Many systems gate the save button and leave the data readable through a neighbouring screen or an export.",
            "**How do staff get accounts?** A directory entry is not a login. Granting access should be a deliberate, recorded act — not a side effect of HR adding a row.",
            "**What happens when someone leaves?** Deactivation should preserve their history and revoke access immediately, including on the mobile app.",
            "**Is there an audit trail you cannot edit?** If an administrator can change or delete an audit record, it is a log, not an audit trail.",
          ],
        },
      ],
    },
    {
      id: "data",
      heading: "Data, exit and hosting",
      blocks: [
        {
          t: "ul",
          items: [
            "**Give me an export I can read without you.** Ask for CSV or XLSX of students, fee ledger and attendance, downloaded during the demo. A vendor who needs a support ticket for that has told you what leaving feels like.",
            "**What does \"delete a student\" do?** The right answer is nuanced — identifying fields redacted, statutory records retained in a restricted state. \"It is gone completely\" is either unconsidered or a compliance problem.",
            "**Where does it run — and can I have that in writing?** Compute, database, storage and backup locations should be documented for your deployment before go-live. Never infer hosting from a marketing site, including ours.",
            "**When was a restore last tested?** And what is the recovery point?",
          ],
        },
      ],
    },
    {
      id: "messaging",
      heading: "Communication",
      blocks: [
        {
          t: "ul",
          items: [
            "**What evidence does each channel return?** In-app can show recorded and read state; WhatsApp can return delivered and read; SMS cannot tell you a parent read anything. A dashboard showing a read rate for SMS is showing a number it cannot know.",
            "**Can the whole system be silenced?** During migration or a crisis you need one control that holds non-essential outbound while transactional messages still work. Ask who can flip it.",
            "**How does a business-initiated WhatsApp message get sent?** Outside a reply window it must be an approved template; free-text broadcasts on demand are not something the channel permits.",
            "**Who owns the sender identity?** The WhatsApp number, SMS sender ID and DLT registrations — whose name are they in?",
          ],
        },
      ],
    },
    {
      id: "ai",
      heading: "AI features",
      blocks: [
        {
          t: "ul",
          items: [
            "**What leaves the building, through how many code paths?** One funnel every model call passes through is checkable. \"Each feature calls the provider\" is not.",
            "**Can an AI action write without a person confirming?** For anything touching money or a parent message the answer should be no — ask to see the confirmation step.",
            "**Which record types are out of scope?** Name health, wellbeing and counselling notes.",
            "**How is AI usage billed?** Ask what happens when the allowance runs out mid-term.",
          ],
        },
      ],
    },
    {
      id: "hard-for-us",
      heading: "The questions we answer badly",
      blocks: [
        {
          t: "p",
          text: "A vendor-written buying guide is arranged so the vendor wins. Here are the questions where our own answers are weak. Ask them of us and of everyone else.",
        },
        {
          t: "ul",
          items: [
            "**\"Name your security certification.\"** We have none to name. We describe concrete controls — database-level tenant isolation, role-based access with record-level teacher scoping, a second approver on money, a hash-chained audit log — and you judge those on their merits. If a certification is a hard requirement, we will not clear it today.",
            "**\"What uptime do you guarantee, and what is the service credit?\"** We publish no uptime figure, no service-level agreement and no response-time promise. Those exist only in a signed schedule — negotiate one in writing rather than reading it off a website, ours or anyone's.",
            "**\"Is the mobile app on the App Store and Play Store?\"** Not yet. It is a real first-party app in controlled early access ahead of public store release. If day-one store availability is required, that is a genuine gap for us.",
            "**\"Does it work offline?\"** No. It is an online product; we do not build offline-first. If attendance is taken where connectivity is unreliable, test that before committing.",
            "**\"How many schools use it?\"** We do not publish customer counts or testimonials, so we cannot answer with a number. What we can do is give you a working workspace with your own data in it, so you judge the product, not the reference list.",
            "**\"Do you verify Aadhaar, or push to DigiLocker live?\"** We do not market Aadhaar e-KYC; identity documents are uploaded and reviewed by staff. DigiLocker is a deployment-specific conversation, not a shipped live connection.",
            "**\"Does it file our EPF, ESI and TDS returns?\"** No product should claim that. Payroll produces the statutory outputs from a locked run; filing on the portals stays a school action.",
          ],
        },
      ],
    },
    {
      id: "scoring",
      heading: "Scoring it afterwards",
      blocks: [
        {
          t: "p",
          text: "Do not score on feature counts; every proposal claims every feature. Score four things you verified: did it do your five weekly jobs on your data; can you get your data out unaided; does a control stop one person doing something irreversible alone; and did you calculate the total annual cost yourself, messages and gateway charges included.",
        },
        {
          t: "p",
          text: "Then check the answers against the product's own pages rather than the deck. For us: [fee collection](/modules/fee-collection), [role and permission](/modules/role-and-permission), [MIS reports](/modules/mis-reports), and the published [pricing](/pricing).",
        },
      ],
    },
  ],
  sources: [],
};

export default article;
